import { createContext, MiddlewareFunction } from 'react-router';
import { match, P } from 'ts-pattern';
import api from '~/utils/api';
import { identity } from '~/utils/compute';
import {
  NetworkError,
  ResponseError,
  StoreNotFoundError,
} from '~/utils/errors';
import { appStoreContext } from '~/utils/routing';
import { storeParamSchema } from '~/utils/schemas';
import { setTheme } from '~/utils/theme';
import { AsyncReturnType } from '~/utils/types';

export type Settings = AsyncReturnType<typeof api.fetchSettings>;
export const settingsContext = createContext<Settings>();

/**
 * Middleware for the app shell route runs before all loaders.
 * Sets the theme and in the future may initialize the store.
 * The context is passed as handler context to all data functions
 * and can be used to share data/state.
 */
export const middleware: MiddlewareFunction = async ({ params, context }) => {
  const appStore = context.get(appStoreContext);

  try {
    const parsedParams = storeParamSchema.safeParse(params);
    const state = appStore.getState();

    // when on a crew route with store and slug does not match state, get the store settings
    if (
      parsedParams.success &&
      parsedParams.data.store !== state.settings?.storeSlug
    ) {
      const slug = parsedParams.data.store;
      const fetchedSettings = await api
        .fetchSettings({
          params: { slug },
        })
        .catch((error) => {
          // if the store is not found, throw a specific error
          if (error instanceof ResponseError && error.response.status === 404) {
            throw new StoreNotFoundError({ slug });
          }

          // rethrow other errors
          throw error;
        });

      appStore.dispatch(appStore.actions.setSettings(fetchedSettings));
    }

    const { settings } = appStore.getState();

    if (settings) {
      context.set(settingsContext, settings);
    }

    if (settings?.theme) {
      setTheme(settings.theme);
    }
  } catch (error) {
    const middlewareError = match(error)
      // ? redirect to non branded lookup instead of error page?
      .with(P.instanceOf(StoreNotFoundError), identity)
      .with(P.instanceOf(ResponseError), identity)
      .with(P.instanceOf(NetworkError), identity)
      .otherwise(
        () =>
          new Error(
            `App Middleware failed: ${error instanceof Error ? error.message : 'unknown error'}`,
            { cause: error },
          ),
      );

    // abort the react router data loading and show an error page
    throw middlewareError;
  }
};
